import { useState, useEffect, useCallback } from 'react'
import { getApiBaseUrl } from '../lib/apiBaseUrl'

interface ConnectionStatus {
  isConnected: boolean
  isChecking: boolean
  error: string | null
  lastChecked: number | null
}

export function useBackendConnection() {
  const [status, setStatus] = useState<ConnectionStatus>({
    isConnected: false,
    isChecking: true,
    error: null,
    lastChecked: null
  })

  const checkConnection = useCallback(async () => {
    setStatus(prev => ({ ...prev, isChecking: true, error: null }))

    const base = getApiBaseUrl()

    try {
      const controller = new AbortController()
      const timeoutId = setTimeout(() => controller.abort(), 5000) // 5 secondes pour le health check

      const response = await fetch(`${base}/api/health`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        signal: controller.signal,
      })

      clearTimeout(timeoutId)

      if (response.ok) {
        setStatus({
          isConnected: true,
          isChecking: false,
          error: null,
          lastChecked: Date.now()
        })
        return true
      } else {
        throw new Error(`Backend répond mais avec une erreur: ${response.status}`)
      }
    } catch (error) {
      let errorMessage = 'Impossible de se connecter au backend'
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        errorMessage = `Impossible de se connecter au serveur backend sur ${base}. Vérifiez que le serveur est démarré.`
      } else if (error instanceof Error && error.name === 'AbortError') {
        errorMessage = `Timeout: Le backend ne répond pas dans les temps (${base})`
      } else if (error instanceof Error) {
        errorMessage = error.message
      }

      setStatus({
        isConnected: false,
        isChecking: false,
        error: errorMessage,
        lastChecked: Date.now()
      })
      return false
    }
  }, [])

  // Vérifier la connexion au montage
  useEffect(() => {
    checkConnection()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Vérifier périodiquement si déconnecté
  useEffect(() => {
    if (status.isConnected || status.isChecking) {
      return
    }

    const interval = setInterval(() => {
      checkConnection()
    }, 30000)

    return () => clearInterval(interval)
  }, [status.isConnected, status.isChecking, checkConnection])

  return {
    ...status,
    checkConnection,
    apiBaseUrl: getApiBaseUrl()
  }
}

